<template>
  <div class="trip-planner">
    <button class="share-trip barlow" @click="openShareModal">Share Trip</button>
    <div v-if="operators.length === 0" class="no-operators-message">
      <!-- <img src="../assets/trip-planner-animation.gif" class="trip-planner-animation" alt="Trip planner animation"> -->
      <LottieAnimation :animation-data="emptyTripPlannerAnimation" :autoplay="true" width="118px" />
      <h2 class="barlow">Umm, you haven't added anything to your trip plan!</h2>
      <p class="figtree">
        To create your own custom trip plan, just favourite places of interest when browsing our
        website by clicking on the heart icon like the example above. Then you can come back and
        view the places you've added here, curate your list and share it.
      </p>
    </div>
    <TransitionGroup
      v-else
      name="operator-cards"
      tag="div"
      class="operator-cards grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      <TripPlannerOperatorCard
        v-for="(operator, index) in operators"
        :key="operator.id"
        :operator="operator"
        :marker-index="index + 1"
        @view-on-map="handleViewOnMap"
        @reorder="handleReorder"
      />
    </TransitionGroup>
  </div>

  <Transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0 scale-95"
    enter-to-class="opacity-100 scale-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="opacity-100 scale-100"
    leave-to-class="opacity-0 scale-95"
  >
    <div v-if="isShareModalOpen" class="filter-popup" @click.self="closeShareModal">
      <div class="filter-popup-inner bg-white p-6 rounded-lg shadow-xl w-96 relative">
        <button @click="closeShareModal" class="filter-close" aria-label="Close modal">
          <svg class="filter-close-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <h2 class="filter-options mb-4 barlow">Share your trip plan</h2>
        <div class="share-trip-description">
          <p class="figtree">
            Use the trip plan feature to shortlist accommodation and things to see and do to create
            the perfect itinerary.
          </p>
          <p class="figtree">Then copy and share it with your friends and family!</p>
        </div>
        <div class="flex share-url-container">
          <input
            type="text"
            class="share-url-input"
            name="share-url"
            id="share-url"
            v-model="constructedUrl"
            readonly
            aria-label="Share URL"
          />
          <button
            class="share-url-button"
            @click="copy(constructedUrl)"
            aria-label="Copy to clipboard"
          >
            <span v-if="!copied">Copy</span>
            <span v-else>Copied!</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { useClipboard } from "@vueuse/core";

import TripPlannerOperatorCard from "./TripPlannerOperatorCard.vue";
import LottieAnimation from "../components/LottieAnimation.vue";

import { useConfigStore } from "../stores";

const config = useConfigStore();

let emptyTripPlannerAnimation = ref(null);

if (!config.features.lottieAnimations) {
  import("../assets/animations/VEG-lottie-tripplan.json").then((module) => {
    emptyTripPlannerAnimation.value = module.default;
  });
} else {
  import("../assets/animations/AW-lottie-tripplan.json").then((module) => {
    emptyTripPlannerAnimation.value = module.default;
  });
}
// import emptyTripPlannerAnimation from '../assets/animations/AW-lottie-tripplan.json'

// Constants
const BASE_URL = config.tripPlannerUrl;
const STORAGE_KEY = "trip_planner_ids";

// Reactive state
const constructedUrl = ref("");
const isShareModalOpen = ref(false);

// Props and emits
const props = defineProps({
  operators: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits([
  "select-operator",
  "view-on-map",
  "toggle-trip-planner",
  "reorder-operators",
]);

// Clipboard functionality
const { copy, copied } = useClipboard({ source: constructedUrl });

/**
 * Constructs the URL with array-like query parameters.
 */
const constructUrlWithIds = (baseUrl, storageKey, paramName = "ids[]") => {
  const storedIdsString = localStorage.getItem(storageKey);
  let tripIds = [];

  try {
    tripIds = JSON.parse(storedIdsString);
    if (!Array.isArray(tripIds)) {
      console.warn(`Expected an array for key "${storageKey}", but got:`, tripIds);
      tripIds = [];
    }
  } catch (error) {
    console.error(`Error parsing JSON from localStorage key "${storageKey}":`, error);
    tripIds = [];
  }

  // Filter out non-integer IDs
  const validTripIds = tripIds.filter((id) => Number.isInteger(id));

  if (validTripIds.length === 0) {
    console.warn("No valid Trip IDs found. Returning base URL without query parameters.");
    return baseUrl;
  }

  // Determine if the base URL already has query parameters
  const hasExistingParams = baseUrl.includes("?");
  const connector = hasExistingParams ? "&" : "?";

  // Construct the query string
  const params = new URLSearchParams();
  validTripIds.forEach((id) => params.append(paramName, id));

  // Return the full URL
  return `${baseUrl}${connector}${params.toString()}`;
};

// Event handlers
const handleViewOnMap = (operator) => {
  emit("view-on-map", operator);
};

const handleReorder = (newOrder) => {
  emit("reorder-operators", newOrder);
};

const openShareModal = () => {
  // Update URL before opening modal
  constructedUrl.value = constructUrlWithIds(BASE_URL, STORAGE_KEY);
  isShareModalOpen.value = true;
};

const closeShareModal = () => {
  isShareModalOpen.value = false;
};

// Lifecycle hooks
onMounted(() => {
  constructedUrl.value = constructUrlWithIds(BASE_URL, STORAGE_KEY);
});

// Watchers
watch(
  () => props.operators,
  (newOperators) => {
    if (newOperators && newOperators.length > 0) {
      const tripPlannerIds = newOperators.map((op) => op.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tripPlannerIds));

      // Update URL when operators change
      constructedUrl.value = constructUrlWithIds(BASE_URL, STORAGE_KEY);
    }
  },
  { deep: true },
);
</script>

<style scoped lang="scss">
.grid {
  display: grid;
  gap: 10px;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

/* Animation styles */
.operator-cards-move {
  transition: transform 0.3s ease;
}

.operator-cards-enter-active,
.operator-cards-leave-active {
  transition: all 0.3s ease;
}

.operator-cards-enter-from,
.operator-cards-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.operator-cards-leave-active {
  position: absolute;
}

/* Ensure items maintain their grid position during animation */
.operator-cards > * {
  transition: all 0.3s ease;
  grid-row: auto;
  grid-column: auto;
}

.share-trip {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: var(--vwc-color-primary);
  color: white;
  border: none;
  border-radius: 9999px;
  margin-bottom: 10px;
}

.share-trip-description {
  font-size: 12px;
}

.share-url-input {
  padding: 10px;
  flex-grow: 1;
  border-radius: 9999px 0 0 9999px;
  border: 1px solid var(--vwc-color-primary);
}

.share-url-button {
  padding: 10px;
  background: var(--vwc-color-primary);
  color: white;
  border: none;
  border-radius: 0 9999px 9999px 0;
  cursor: pointer;
}

.no-operators-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
}

.trip-planner-animation {
  width: 100%;
  max-width: 200px;
}
</style>
